const ROOT_SELECTOR = '[data-tc-product-media]';
const VALID_PANELS = new Set(['overview', 'video', 'dimensions']);

/*
 * Product media architecture:
 * - Overview is rendered and controlled by Shopify's native product gallery.
 * - Video is isolated inside the dedicated Video panel.
 * - Dimensions is isolated inside the dedicated Dimensions panel.
 * - Custom panels share only visual design tokens; they never modify Overview.
 */

function setBodyLock() {
  const hasOpenDialog = document.querySelector('.tc-image-dialog[open]');

  document.body.classList.toggle(
    'tc-product-media-dialog-open',
    Boolean(hasOpenDialog)
  );
}


/* =========================================================
 * Overview stage geometry sync (layout only)
 *
 * This reads only the rendered Overview stage size/offset so Video and
 * Dimensions can use the same visual stage. It never reads, reorders,
 * replaces, or overlays Overview media content.
 * ========================================================= */

function initOverviewStageSync(root, signal) {
  const overviewPanel = root.querySelector('[data-tc-panel="overview"]');
  if (!overviewPanel) return null;

  const getStage = () =>
    overviewPanel.querySelector(
      'media-gallery.product-media-gallery slideshow-container'
    ) || overviewPanel.querySelector('slideshow-container');

  let resizeObserver = null;

  const sync = () => {
    const stage = getStage();
    if (!stage) return;

    const rootRect = root.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();

    // Keep the last valid geometry while Overview is hidden.
    if (
      rootRect.width <= 0 ||
      stageRect.width <= 0 ||
      stageRect.height <= 0
    ) {
      return;
    }

    const offset = Math.max(0, stageRect.left - rootRect.left);
    const safeOffset = Math.min(offset, rootRect.width);
    const safeWidth = Math.min(stageRect.width, rootRect.width - safeOffset);

    root.style.setProperty('--tc-overview-stage-offset', `${safeOffset}px`);
    root.style.setProperty('--tc-overview-stage-width', `${safeWidth}px`);
    root.style.setProperty(
      '--tc-media-stage-ratio',
      `${stageRect.width} / ${stageRect.height}`
    );
  };

  sync();

  const stage = getStage();
  if (stage && 'ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(sync);
    resizeObserver.observe(stage);
  }

  window.addEventListener('resize', sync, { signal });

  return {
    sync,
    destroy() {
      resizeObserver?.disconnect();
    },
  };
}

/* =========================================================
 * Dimensions gallery
 * ========================================================= */

function initDimensionsGallery(root, signal) {
  const gallery = root.querySelector('[data-tc-dimensions-gallery]');
  if (!gallery || gallery.dataset.tcReady === 'true') return null;

  gallery.dataset.tcReady = 'true';

  const slides = [...gallery.querySelectorAll('[data-tc-dimension-slide]')];
  const thumbs = [...gallery.querySelectorAll('[data-tc-dimension-thumb]')];

  const show = (index) => {
    if (!slides.length) return;

    const safeIndex = Math.max(0, Math.min(index, slides.length - 1));

    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === safeIndex;
      slide.hidden = !active;
      slide.classList.toggle('is-active', active);
    });

    thumbs.forEach((thumb, thumbIndex) => {
      const active = thumbIndex === safeIndex;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', String(active));
      thumb.setAttribute('aria-selected', String(active));
      thumb.tabIndex = active ? 0 : -1;

      if (active) {
        thumb.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    });
  };

  thumbs.forEach((thumb) => {
    thumb.addEventListener(
      'click',
      () => {
        show(Number(thumb.dataset.index || 0));
      },
      { signal }
    );
  });

  show(0);

  return { show };
}

/* =========================================================
 * Dedicated video gallery
 * ========================================================= */

function destroyVideoGallery(gallery) {
  if (!gallery) return;

  gallery.querySelectorAll('video').forEach((video) => {
    try {
      video.pause();
      video.currentTime = 0;
    } catch (error) {
      console.debug('Unable to reset product video.', error);
    }
  });

  gallery.querySelectorAll('iframe').forEach((iframe) => {
    iframe.src = 'about:blank';
  });

  gallery.querySelectorAll('[data-tc-video-player]').forEach((player) => {
    player.replaceChildren();
  });

  gallery.querySelectorAll('[data-tc-video-slide]').forEach((slide) => {
    slide.classList.remove('is-playing');
  });
}

function initVideoGallery(root, signal) {
  const gallery = root.querySelector('[data-tc-video-gallery]');
  if (!gallery || gallery.dataset.tcReady === 'true') return null;

  gallery.dataset.tcReady = 'true';

  const slides = [...gallery.querySelectorAll('[data-tc-video-slide]')];
  const thumbs = [...gallery.querySelectorAll('[data-tc-video-thumb]')];

  const show = (index) => {
    if (!slides.length) return;

    const safeIndex = Math.max(0, Math.min(index, slides.length - 1));

    destroyVideoGallery(gallery);

    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === safeIndex;
      slide.hidden = !active;
      slide.classList.toggle('is-active', active);
    });

    thumbs.forEach((thumb, thumbIndex) => {
      const active = thumbIndex === safeIndex;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', String(active));
      thumb.setAttribute('aria-selected', String(active));
      thumb.tabIndex = active ? 0 : -1;

      if (active) {
        thumb.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    });
  };

  const play = (slide) => {
    if (!slide) return;

    const template = slide.querySelector('[data-tc-video-template]');
    const player = slide.querySelector('[data-tc-video-player]');

    if (!template || !player) return;

    destroyVideoGallery(gallery);

    player.replaceChildren(template.content.cloneNode(true));
    slide.classList.add('is-playing');

    const video = player.querySelector('video');
    if (video) {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;

      video.play().catch(() => {
        // Some browsers can require a second user interaction.
      });
    }
  };

  thumbs.forEach((thumb) => {
    thumb.addEventListener(
      'click',
      () => {
        show(Number(thumb.dataset.index || 0));
      },
      { signal }
    );
  });

  slides.forEach((slide) => {
    slide.querySelector('[data-tc-video-play]')?.addEventListener(
      'click',
      () => {
        play(slide);
      },
      { signal }
    );
  });

  show(0);

  return {
    destroy: () => destroyVideoGallery(gallery),
  };
}

/* =========================================================
 * Root component
 * ========================================================= */

function initRoot(root) {
  if (!root || root.dataset.tcReady === 'true') return;

  root.dataset.tcReady = 'true';

  const controller = new AbortController();
  const { signal } = controller;

  const overviewStage = initOverviewStageSync(root, signal);
  initDimensionsGallery(root, signal);
  const videoGallery = initVideoGallery(root, signal);

  const pageButtons = [...root.querySelectorAll('[data-tc-page-action]')];
  const panels = [...root.querySelectorAll('[data-tc-panel]')];

  const imageDialog = root.querySelector('[data-tc-image-dialog]');
  const imageDialogImage = root.querySelector('[data-tc-image-dialog-image]');

  let activePanel = root.dataset.activePanel || 'overview';

  if (!VALID_PANELS.has(activePanel)) {
    activePanel = 'overview';
  }

  const setPanel = (name) => {
    if (!VALID_PANELS.has(name)) return;

    // Capture the exact Overview stage geometry before hiding it.
    if (activePanel === 'overview' && name !== 'overview') {
      overviewStage?.sync();
    }

    if (activePanel === 'video' && name !== 'video') {
      videoGallery?.destroy();
    }

    activePanel = name;
    root.dataset.activePanel = name;

    panels.forEach((panel) => {
      const active = panel.dataset.tcPanel === name;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });

    pageButtons.forEach((button) => {
      const active = button.dataset.tcPageAction === name;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    if (name === 'overview') {
      requestAnimationFrame(() => overviewStage?.sync());
    }
  };

  pageButtons.forEach((button) => {
    button.addEventListener(
      'click',
      () => {
        setPanel(button.dataset.tcPageAction);
      },
      { signal }
    );
  });

  root.querySelectorAll('[data-tc-open-image]').forEach((button) => {
    button.addEventListener(
      'click',
      () => {
        if (!imageDialog || !imageDialogImage || !button.dataset.imageSrc) {
          return;
        }

        imageDialog.__tcOpener = button;
        imageDialogImage.src = button.dataset.imageSrc;
        imageDialogImage.alt = button.dataset.imageAlt || '';

        if (typeof imageDialog.showModal === 'function') {
          imageDialog.showModal();
        } else {
          imageDialog.setAttribute('open', '');
        }

        setBodyLock();

        requestAnimationFrame(() => {
          imageDialog.querySelector('[data-tc-image-close]')?.focus();
        });
      },
      { signal }
    );
  });

  const closeImage = () => {
    if (!imageDialog) return;

    const opener = imageDialog.__tcOpener;

    if (typeof imageDialog.close === 'function' && imageDialog.open) {
      imageDialog.close();
      return;
    }

    imageDialog.removeAttribute('open');
    imageDialog.__tcOpener = null;
    imageDialogImage?.removeAttribute('src');
    setBodyLock();

    requestAnimationFrame(() => opener?.focus?.());
  };

  root.querySelector('[data-tc-image-close]')?.addEventListener(
    'click',
    closeImage,
    { signal }
  );

  imageDialog?.addEventListener(
    'cancel',
    (event) => {
      event.preventDefault();
      closeImage();
    },
    { signal }
  );

  imageDialog?.addEventListener(
    'click',
    (event) => {
      if (event.target === imageDialog) closeImage();
    },
    { signal }
  );

  imageDialog?.addEventListener(
    'close',
    () => {
      const opener = imageDialog.__tcOpener;
      imageDialog.__tcOpener = null;
      imageDialogImage?.removeAttribute('src');
      setBodyLock();
      requestAnimationFrame(() => opener?.focus?.());
    },
    { signal }
  );

  setPanel(activePanel);

  root.__tcProductMediaDestroy = () => {
    videoGallery?.destroy();
    overviewStage?.destroy();
    controller.abort();

    root.dataset.tcReady = 'false';
    delete root.__tcProductMediaDestroy;
  };
}

/* =========================================================
 * Global lifecycle
 * ========================================================= */

function initAll(scope = document) {
  if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) {
    initRoot(scope);
  }

  scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
}

function destroyAll(scope) {
  if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) {
    scope.__tcProductMediaDestroy?.();
  }

  scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => {
    root.__tcProductMediaDestroy?.();
  });
}

initAll();

document.addEventListener('shopify:section:load', (event) => {
  initAll(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyAll(event.target);
});
s